import { type ObjectMode } from '@databases/types';
import {
  type BasicDatabase,
  type BrokenDatabase,
  type Database,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseWithoutEntries,
  type ShallowDatabase,
} from '@databases/types/database';

import { UnfoundFieldError } from '../errors';

export function isBrokenDatabase(
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): database is BrokenDatabase {
  return 'error' in database;
}

export function isNonBrokenDatabase<T extends Database | ShallowDatabase | BasicDatabase>(
  database: T | BrokenDatabase
): database is T {
  return !isBrokenDatabase(database);
}

export function isShallowDatabase(
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): database is ShallowDatabase {
  return !isBasicDatabase(database) && !isNonShallowOrBrokenDatabase(database);
}

export function isNonShallowDatabase(
  database: Database | ShallowDatabase | BrokenDatabase
): database is Database {
  return 'entries' in database;
}

export function isNonShallowOrBrokenDatabase(
  database: Database | ShallowDatabase | BrokenDatabase
): database is Database | BrokenDatabase {
  return isBrokenDatabase(database) || isNonShallowDatabase(database);
}

export function isBasicDatabase(
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): database is BasicDatabase {
  return !('id' in database) && !('error' in database);
}

export function isNonBasicDatabase(
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): database is ShallowDatabase | Database | BrokenDatabase {
  return !isBasicDatabase(database);
}

export function getDatabaseFieldOptional(
  database: DatabaseWithoutEntries<ObjectMode>,
  fieldId: string | undefined
): DatabaseField | undefined {
  return fieldId == null ? fieldId : database.fieldsById[fieldId];
}

export function getDatabaseFieldRequired(
  database: DatabaseWithoutEntries<ObjectMode>,
  fieldId: string
): DatabaseField {
  const field = getDatabaseFieldOptional(database, fieldId);
  if (!field) {
    throw new UnfoundFieldError(`Could not find database field ${fieldId}`, fieldId);
  }

  if (
    field.type === DatabaseFieldType.PAGE_LINK &&
    field.settings.creatable === undefined
  ) {
    return {
      ...field,
      settings: {
        ...field.settings,
        creatable: true,
      },
    };
  }
  return field;
}
